import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, Eye, Search } from "lucide-react";
import UserAvatar from "../ui/UserAvatar";
import { fetchNoticeReceipts } from "../../controllers/noticeController";
import { RateBar, noticeInput } from "./NoticeParts";
import { formatNoticeDateTime, formatNoticeTime } from "./noticeUtils";

const FILTERS = {
  all: { label: "Everyone", test: () => true },
  unread: { label: "Not read", test: (p) => !p.readAt },
  read: { label: "Read", test: (p) => p.readAt },
  pending: { label: "Not acknowledged", test: (p) => !p.acknowledgedAt, ackOnly: true },
  acknowledged: { label: "Acknowledged", test: (p) => p.acknowledgedAt, ackOnly: true },
};

/** HR/admins: who has read and acknowledged a notice. */
function NoticeReceipts({ noticeId, requiresAck, version }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState(requiresAck ? "pending" : "unread");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchNoticeReceipts(noticeId)
      .then((result) => !cancelled && setData(result))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [noticeId, version]);

  const people = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.people.filter(
      (p) =>
        FILTERS[filter].test(p) &&
        (!q || [p.name, p.jobTitle, p.department].some((v) => v?.toLowerCase().includes(q)))
    );
  }, [data, filter, query]);

  if (error) return <p className="text-sm text-app-error">Couldn’t load read receipts.</p>;
  if (!data) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-lg bg-app-hover" />
        ))}
      </div>
    );
  }

  const counts = Object.fromEntries(Object.entries(FILTERS).map(([key, f]) => [key, data.people.filter(f.test).length]));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row">
        <RateBar label="Read" value={data.readCount} total={data.audience} />
        {requiresAck && <RateBar label="Acknowledged" value={data.ackCount} total={data.audience} tone="green" />}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {Object.entries(FILTERS)
          .filter(([, f]) => requiresAck || !f.ackOnly)
          .map(([key, f]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                filter === key
                  ? "border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500"
                  : "border-app-primary text-app-secondary hover:bg-app-hover"
              }`}
            >
              {f.label} <span className="tabular-nums opacity-80">{counts[key]}</span>
            </button>
          ))}
      </div>

      <label className="relative block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-muted" aria-hidden="true" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people"
          className={`${noticeInput} pl-9`}
          aria-label="Search people"
        />
      </label>

      {people.length === 0 ? (
        <p className="py-6 text-center text-sm text-app-muted">
          {filter === "pending" || filter === "unread" ? "Everyone's caught up 🎉" : "Nobody here yet."}
        </p>
      ) : (
        <ul className="divide-y divide-app-primary rounded-xl border border-app-primary">
          {people.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
              <UserAvatar name={p.name} src={p.avatarThumbUrl} seed={p.id} size="sm" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-app-primary">{p.name}</p>
                <p className="truncate text-xs text-app-muted">
                  {[p.jobTitle, p.department].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <div className="shrink-0 text-right text-xs">
                {requiresAck && p.acknowledgedAt ? (
                  <span className="inline-flex items-center gap-1 font-semibold text-emerald-600 dark:text-emerald-400" title={formatNoticeDateTime(p.acknowledgedAt)}>
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> {formatNoticeTime(p.acknowledgedAt)}
                  </span>
                ) : p.readAt ? (
                  <span className="inline-flex items-center gap-1 text-app-secondary" title={formatNoticeDateTime(p.readAt)}>
                    <Eye className="h-3.5 w-3.5" aria-hidden="true" /> Read {formatNoticeTime(p.readAt)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-app-muted">
                    <Circle className="h-3.5 w-3.5" aria-hidden="true" /> Not read
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default NoticeReceipts;
